// PandaScore API Service
// Vercel Functionsのエンドポイントを使用

class PandaScoreService {
  constructor() {
    // 環境に応じて適切なAPIを使用
    const hostname = window.location.hostname;
    const origin = window.location.origin;
    
    // 本番環境の判定（clashsync.jpまたはwww.clashsync.jp）
    const isProduction = hostname === 'clashsync.jp' || hostname === 'www.clashsync.jp';
    const isVercelPreview = hostname.includes('vercel.app');
    
    if (isProduction) {
      // 本番環境でもVercel Functionsを使用
      this.baseUrl = '/api';
      console.log('Production environment detected, using Vercel Functions');
    } else if (isVercelPreview) {
      // Vercelプレビュー環境では絶対URLを使用
      this.baseUrl = `${origin}/api`;
      console.log('Vercel preview environment detected');
    } else {
      // ローカル開発環境
      this.baseUrl = '/api';
      console.log('Local development environment detected');
    }
    
    console.log('PandaScoreService initialized:', {
      hostname,
      origin,
      isProduction,
      isVercelPreview,
      baseUrl: this.baseUrl,
      environment: isProduction ? 'production' : (isVercelPreview ? 'vercel-preview' : 'local')
    });
  }

  // VALORANTの大会一覧を取得
  async getValorantTournaments(params = {}) {
    try {
      console.log('getValorantTournaments called with params:', params);
      
      const queryParams = new URLSearchParams({
        pageSize: params.pageSize || 20,
        page: params.page || 1,
      });

      // 日付範囲フィルター
      if (params['range[begin_at]']) {
        queryParams.append('beginAtRange', params['range[begin_at]']);
      }

      // その他のパラメータ
      Object.keys(params).forEach(key => {
        if (!['pageSize', 'page', 'range[begin_at]'].includes(key)) {
          queryParams.append(key, params[key]);
        }
      });

      // 必ずVercelのAPIエンドポイントを使用
      const url = `${this.baseUrl}/tournaments?${queryParams}`;
      console.log('Fetching from URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        let errorMessage = `API error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // JSON parse error
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch Valorant tournaments:', error);
      throw error;
    }
  }

  // 特定の大会の詳細情報を取得
  async getTournamentDetails(tournamentId) {
    try {
      const isFirebase = this.baseUrl.includes('cloudfunctions.net');
      const url = isFirebase 
        ? `${this.baseUrl}/tournaments/${tournamentId}`
        : `${this.baseUrl}/pandascore/tournaments/${tournamentId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch tournament details:', error);
      throw error;
    }
  }

  // 大会のマッチ情報を取得
  async getTournamentMatches(tournamentId, params = {}) {
    try {
      const isFirebase = this.baseUrl.includes('cloudfunctions.net');
      
      if (isFirebase) {
        // Firebase Functions用のエンドポイント
        const queryParams = new URLSearchParams({
          sort: params.sort || 'scheduled_at',
          pageSize: params.pageSize || 50,
          page: params.page || 1,
        });
        
        const response = await fetch(
          `${this.baseUrl}/tournaments/${tournamentId}/matches?${queryParams}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `API error: ${response.status}`);
        }
        
        return await response.json();
      } else {
        // Vercel Functions用のエンドポイント
        const queryParams = new URLSearchParams({
          'filter[tournament_id]': tournamentId,
          'sort': 'scheduled_at',
          'page[size]': params.pageSize || 50,
          'page[number]': params.page || 1,
        });

        const response = await fetch(
          `${this.baseUrl}/pandascore/matches?${queryParams}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `API error: ${response.status}`);
        }

        return await response.json();
      }
    } catch (error) {
      console.error('Failed to fetch tournament matches:', error);
      throw error;
    }
  }

  // PandaScoreのデータを内部フォーマットに変換
  convertToInternalFormat(pandaScoreTournament, matches = []) {
    const tournament = {
      // 基本情報
      tournamentName: pandaScoreTournament.name,
      date: pandaScoreTournament.begin_at ? 
        new Date(pandaScoreTournament.begin_at).toISOString().split('T')[0] : 
        new Date().toISOString().split('T')[0],
      category: 'VALORANT',
      
      // 時間情報
      startTime: pandaScoreTournament.begin_at ? 
        new Date(pandaScoreTournament.begin_at).toTimeString().slice(0, 5) : '',
      endTime: pandaScoreTournament.end_at ? 
        new Date(pandaScoreTournament.end_at).toTimeString().slice(0, 5) : '',
      
      // 会場情報（APIから取得できない場合は手動入力が必要）
      venue: pandaScoreTournament.prizepool ? `賞金総額: ${pandaScoreTournament.prizepool}` : 'オンライン',
      venueLocation: 'オンライン',
      
      // 公式サイト
      officialSite: pandaScoreTournament.league?.url || '',
      
      // 下書きフラグ
      isDraft: true,
      
      // PandaScore情報
      pandaScoreId: pandaScoreTournament.id,
      pandaScoreSlug: pandaScoreTournament.slug,
      
      // メタ情報
      createdFrom: 'pandascore',
      importedAt: new Date().toISOString(),
    };

    // 対戦カード情報の変換
    if (matches.length > 0) {
      tournament.matchups = matches.map(match => ({
        round: match.name || `Match ${match.match_type}`,
        boFormat: match.number_of_games ? `BO${match.number_of_games}` : '',
        teams: [
          { name: match.opponents?.[0]?.opponent?.name || 'TBD' },
          { name: match.opponents?.[1]?.opponent?.name || 'TBD' }
        ],
        scheduledAt: match.scheduled_at,
        pandaScoreMatchId: match.id
      }));
    } else {
      tournament.matchups = [];
    }

    // 配信情報の変換
    if (pandaScoreTournament.streams_list && pandaScoreTournament.streams_list.length > 0) {
      tournament.broadcast = pandaScoreTournament.streams_list.map(stream => ({
        platform: this.getPlatformName(stream.raw_url),
        url: stream.raw_url
      }));
    } else {
      tournament.broadcast = [];
    }

    return tournament;
  }

  // URLからプラットフォーム名を推定
  getPlatformName(url) {
    if (!url) return 'その他';
    
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'YouTube';
    if (url.includes('twitch.tv')) return 'Twitch';
    if (url.includes('nicovideo.jp')) return 'ニコニコ生放送';
    if (url.includes('openrec.tv')) return 'OPENREC';
    
    return 'その他';
  }

  // 今後の大会を取得（日付フィルタリング付き）
  async getUpcomingValorantTournaments(daysAhead = 30) {
    try {
      console.log('getUpcomingValorantTournaments called with days:', daysAhead);
      
      // Firebase Functionsの場合はエンドポイントが異なる
      const isFirebase = this.baseUrl.includes('cloudfunctions.net');
      const url = isFirebase 
        ? `${this.baseUrl}/tournaments/upcoming/${daysAhead}`
        : `${this.baseUrl}/tournaments-upcoming?days=${daysAhead}`;
      console.log('Fetching from URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        let errorMessage = `API error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // JSON parse error
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch upcoming tournaments:', error);
      throw error;
    }
  }
}

// シングルトンインスタンスをエクスポート
export const pandaScoreService = new PandaScoreService();
